import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useGame } from '../../contexts/GameContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EnhancedRummyTable from '../EnhancedRummyTable/EnhancedRummyTable';
import SoundControls from '../SoundControls';
import CardUtils from '../../utils/CardUtils';
import HintPanel from './components/HintPanel';
import GameTour from './components/GameTour';
import './EnhancedRummyGame.css';

const EnhancedRummyGame = ({ 
  user, 
  onGameEnd,
  gameId = null
}) => {
  // If gameId is not provided as prop, try to get it from URL params
  const params = useParams();
  const actualGameId = gameId || params.gameId || params.id;
  
  console.log('🎮 EnhancedRummyGame - Initializing with gameId:', actualGameId);
  
    // Get auth context
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Cash game info from location state (passed by CashRummyWaitingRoom)
  const cashGameInfo = location.state?.isCashGame ? location.state : null;
  
  // Get game context for multiplayer
  const gameContext = useGame();

  // Extract values from game context
  const {
    gameState: multiplayerGameState,
    players: multiplayerPlayers,
    gameStatus: multiplayerGameStatus,
    loading: multiplayerLoading,
    error: multiplayerError,
    socket,
  } = gameContext;

    // Use multiplayer state
  const gameState = multiplayerGameState;
  const loading = multiplayerLoading;
  const error = multiplayerError;

  // ==================== PRACTICE (VS AI) SUPPORT ====================
  // Practice games use an in-memory gameId ("practice_..."). Detect them so we can
  // (1) re-request state on mount and (2) show the Hint assistant.
  const isPracticeGame = !!actualGameId && String(actualGameId).startsWith('practice_');
  const myPlayer = gameState?.players?.find(p => String(p.userId) === String(currentUser?.id));
  const myHand = myPlayer?.cards || [];
  const discardTop = gameState?.discardPile?.length ? gameState.discardPile[gameState.discardPile.length - 1] : null;
  const wildJokerCard = gameState?.wildJoker || null;

  // Re-request state on mount for practice games (in-memory engine) so the table
  // reliably loads even if the initial game-state-initial was missed.
  useEffect(() => {
    if (isPracticeGame && socket) {
      socket.emit('request-game-state', { gameId: actualGameId });
    }
  }, [isPracticeGame, actualGameId, socket]);

  // ==================== GUIDED TOUR (first-time onboarding) ====================
  const [showTour, setShowTour] = useState(false);

  const tourSteps = [
    { target: '[data-tour="joker"]', title: 'Wild Joker', description: `This is the wild joker (${wildJokerCard?.rank || '?'}). Every ${wildJokerCard?.rank || '?'} card is a joker this game — use it to complete sets & sequences. A discarded joker loses its joker status.` },
    { target: '[data-tour="draw"]', title: 'Draw Card', description: 'Tap the deck to draw a card at the start of your turn.' },
    { target: '[data-tour="discard"]', title: 'Discard Card', description: 'Select a card from your hand, then tap Discard to end your turn.' },
    { target: '[data-tour="hand"]', title: 'Your Hand', description: 'Your cards are shown here. Tap a card to select it.' },
    { target: '[data-tour="arrange"]', title: 'Arrange Cards', description: 'Tap Arrange to sort your cards into suits and spot sets & sequences.' },
    { target: '[data-tour="timer"]', title: 'Game Timer', description: 'This countdown shows the time left on the current turn.' },
    { target: '[data-tour="settings"]', title: 'Settings', description: 'Open settings to replay this tutorial anytime.' },
    { target: '[data-tour="chat"]', title: 'Chat', description: 'Use voice chat to talk with other players in multiplayer games.' },
    { target: '[data-tour="leave"]', title: 'Leave Game', description: 'Tap Leave to exit the current game safely.' },
  ];

  const handleTourFinish = useCallback(() => {
    try { localStorage.setItem('rummy_game_tour_seen', '1'); } catch (e) { /* ignore */ }
    setShowTour(false);
  }, []);

  // Replay from the settings menu (custom event).
  useEffect(() => {
    const replay = () => setShowTour(true);
    window.addEventListener('replay-tutorial', replay);
    return () => window.removeEventListener('replay-tutorial', replay);
  }, []);

  // Auto-show once on first entry, after the table has had time to render.
  useEffect(() => {
    if (!isPracticeGame) return;
    let seen = false;
    try { seen = !!localStorage.getItem('rummy_game_tour_seen'); } catch (e) { /* ignore */ }
    if (seen) return;
    const t = setTimeout(() => setShowTour(true), 800);
    return () => clearTimeout(t);
  }, [isPracticeGame]);

  // ==================== UNIFIED SEATING PHASE ====================

    const [seatingCountdown, setSeatingCountdown] = useState(0);
  const [showSeatingUnified, setShowSeatingUnified] = useState(false);
  const seatingTimerRef = useRef(null);
  const seatingStartedRef = useRef(false);

  // Extract seating data from GameContext (synchronous read during render)
  const seatingPhaseData = (() => {
    if (!multiplayerGameState || multiplayerGameState.status !== 'seating') return null;
    const seatingInfo = multiplayerGameState.seatingPhase;
    if (!seatingInfo || !seatingInfo.seatingCards) return null;
    return seatingInfo;
  })();

  const buildSeatingPlayers = useCallback((seatingCards, allPlayers) => {
    if (!Array.isArray(seatingCards)) return [];
    return seatingCards.map(sc => {
      const playerInfo = allPlayers?.find(p => String(p.userId) === String(sc.userId));
      return {
        userId: sc.userId,
        username: playerInfo?.username || 'Player',
        card: {
          id: `seating_spade_${sc.rank}`,
          suit: sc.suit || 'spades',
          rank: sc.rank,
          value: 0,
          isRed: false,
          isJoker: false,
          isSeatingCard: true
        }
      };
    });
  }, []);

    // When seating data becomes available from GameContext, show the unified screen
    useEffect(() => {
    if (!seatingPhaseData || seatingStartedRef.current) return;
    
    // Mark as started immediately to prevent re-entry
    seatingStartedRef.current = true;
    
    console.log('🪑 [UNIFIED] Showing seating phase directly on loading screen');
    setShowSeatingUnified(true);
    
    // Start countdown: convert server ms to seconds
    const rawCountdown = seatingPhaseData.countdown || 15000;
    let seconds = Math.max(1, Math.ceil(rawCountdown / 1000));
    setSeatingCountdown(seconds);
    
    // Local countdown interval (cleanup only on unmount, not on deps change)
    if (seatingTimerRef.current) clearInterval(seatingTimerRef.current);
    seatingTimerRef.current = setInterval(() => {
      setSeatingCountdown(prev => {
        if (prev <= 1) {
          if (seatingTimerRef.current) {
            clearInterval(seatingTimerRef.current);
            seatingTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
   
  }, [seatingPhaseData]);
  

  // A different game id = a different game (e.g. Points Rummy "Play again"): let the new game show its own seating.
  useEffect(() => {
    seatingStartedRef.current = false;
    setShowSeatingUnified(false);
  }, [actualGameId]);

  // When seating ends (status changes to 'playing'), transition to game table
  useEffect(() => {
    if ((multiplayerGameStatus === 'playing' || multiplayerGameStatus === 'started') && showSeatingUnified) {
      console.log('🎮 Seating ended, transitioning to game table');
      setShowSeatingUnified(false);
      setShowGameTable(true);
      setGameInitialized(true);
    }
  }, [multiplayerGameStatus, showSeatingUnified]);

  console.log('🎮 Game Context:', {
    gameStatus: multiplayerGameStatus,
    hasGameState: !!gameState,
    loading,
    error,
    hasSocket: !!socket,
    actualGameId,
    hasSeatingData: !!seatingPhaseData
  });

  // State
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [screenDimensions, setScreenDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [showGameTable, setShowGameTable] = useState(false);
  const [gameInitialized, setGameInitialized] = useState(false);

  // Auto-show game table after short delay (fallback, only if NOT in seating)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!showSeatingUnified) {
        console.log('🚀 Force showing game table after delay');
        setShowGameTable(true);
        setGameInitialized(true);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [showSeatingUnified]);

  // Show game table when playing status arrives (backup)
  useEffect(() => {
    if ((multiplayerGameStatus === 'playing' || multiplayerGameStatus === 'started') && !showGameTable) {
      console.log('✅ Playing status received, showing table');
      setShowSeatingUnified(false);
      setShowGameTable(true);
      setGameInitialized(true);
    }
  }, [multiplayerGameStatus, showGameTable]);

  // Detect mobile device and setup responsive layout
  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || width <= 768;
      const landscape = width > height;
      
      setIsMobile(mobile);
      setIsLandscape(landscape);
      setScreenDimensions({ width, height });
    };

    updateDimensions();
    
    const handleResize = () => {
      updateDimensions();
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

    // Handle leave game
  const handleLeaveGame = () => {
    console.log('🚪 Leaving game...');
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => console.log('Error exiting fullscreen:', err));
    }
    navigate('/lobby');
  };

  // ==================== GET WINNER NAME ====================
  const getWinnerName = () => {
    if (!seatingPhaseData) return 'Unknown';
    const firstId = seatingPhaseData.firstPlayer || seatingPhaseData.firstPlayerId;
    const allPlayers = multiplayerGameState?.players || [];
    const winner = allPlayers.find(p => String(p.userId) === String(firstId));
    return winner?.username || 'Unknown';
  };

      // ==================== RENDER GAME TABLE ====================
  const renderGameTable = () => {
    return (
      <div className="game-main-container" style={{
                    width: '100vw',
                    height: '100vh',
                    height: '100dvh',
                    overflow: 'hidden',
                    touchAction: 'manipulation',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    backgroundColor: '#0a0a1a'
                  }}>
        {/* Sound toggle now lives in the table's left side bar */}
        <EnhancedRummyTable 
          key={actualGameId}
          user={currentUser}
          onGameEnd={() => {
            console.log('🎮 Game ended');
            navigate('/lobby');
          }}
                    gameId={actualGameId}
          isSingleWindow={true}
          screenDimensions={screenDimensions}
          isMobile={isMobile}
          isLandscape={isLandscape}
          onLeaveGame={handleLeaveGame}
          cashGameInfo={cashGameInfo}
          roomConfig={location.state || {}}
        />
        {isPracticeGame && (
          <HintPanel hand={myHand} discardTop={discardTop} wildJoker={wildJokerCard} />
        )}
        {isPracticeGame && showTour && (
          <GameTour steps={tourSteps} onFinish={handleTourFinish} />
        )}
      </div>
    );
  };

  // ==================== RENDER UNIFIED SEATING SCREEN ====================

  const renderSeatingUnified = () => {
    if (!seatingPhaseData) return null;
    
    const seatingCards = seatingPhaseData.seatingCards || [];
    const allPlayers = multiplayerGameState?.players || [];
    const firstPlayerId = seatingPhaseData.firstPlayer || seatingPhaseData.firstPlayerId;
    const players = buildSeatingPlayers(seatingCards, allPlayers);
    
    return (
      <div className="game-launch-screen loading">
        {!isPracticeGame && <SoundControls />}
        <div className="seating-unified">
          <h2>🪑 Seating Phase</h2>
          <p className="subtitle">Players drew spade cards to decide seating order</p>
          
          <div className="seating-cards-container">
            {players.map(player => {
              const isWinner = String(player.userId) === String(firstPlayerId);
              const cardImg = CardUtils.getCardImage(player.card);
              return (
                <div key={player.userId} className={`seating-player-card ${isWinner ? 'winner' : ''}`}>
                  <img 
                    src={cardImg || '/images/cards/back.png'} 
                    alt={player.card.rank} 
                    draggable={false}
                  />
                  <div className="name">{player.username}</div>
                                    {isWinner && <div className="winner-badge">♠️ will play first</div>}
                </div>
              );
            })}
          </div>
          
          {firstPlayerId && (
            <div className="seating-winner-announcement">
              ♠️ {getWinnerName()} will play first
            </div>
          )}
          
          <div className="seating-countdown-timer">
            Game starts in {seatingCountdown}s
          </div>
          
        </div>
      </div>
    );
  };

  // ==================== RENDER GENERIC LOADING SCREEN ====================
  // Only shown briefly when there's NO seating data yet
  const renderGenericLoading = () => {
    return (
      <div className="game-launch-screen loading">
        {!isPracticeGame && <SoundControls />}
        <div className="launch-container">
          <div className="launch-header">
            <div className="launch-icon">♠️</div>
            <h2>Loading Game...</h2>
            <p className="launch-subtitle">
              {!socket ? 'Connecting to server...' : 'Preparing your game'}
            </p>
          </div>
          <div className="loading-progress">
            <div className="progress-spinner">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER LOGIC ====================
  console.log('🎮 Render Decision:', {
    showGameTable,
    gameInitialized,
    actualGameId,
    hasSocket: !!socket,
    gameStatus: multiplayerGameStatus,
    showSeatingUnified,
    hasSeatingData: !!seatingPhaseData,
    seatingCountdown
  });

  // If we have no game ID, show error
  if (!actualGameId) {
    return (
      <div className="game-launch-screen error">
        <div className="launch-container">
          <div className="launch-header">
            <div className="launch-icon">⚠️</div>
            <h2>Invalid Game</h2>
            <p className="launch-subtitle">No game ID provided</p>
          </div>
          <button className="launch-btn primary" onClick={() => navigate('/lobby')}>
            🏠 Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="game-launch-screen error">
        <div className="launch-container">
          <div className="launch-header">
            <div className="launch-icon">⚠️</div>
            <h2>Game Error</h2>
            <p className="launch-subtitle">{error}</p>
          </div>
          <div className="error-actions">
            <button onClick={() => window.location.reload()}>🔄 Retry</button>
            <button onClick={handleLeaveGame}>🚪 Exit</button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== RENDER LOGIC ====================
  // Render persistent game table directly for all phases (waiting, seating, playing)
  if (actualGameId) {
    return renderGameTable();
  }

  // Otherwise show brief generic loading (while waiting for gameId)
  return renderGenericLoading();
};

export default EnhancedRummyGame;